/* Krishna Furniture 3D — lazy three.js viewers. three.js is only downloaded when a viewer is about to be seen. */
const KF = window.KF || {};
const viewers = Array.from(document.querySelectorAll('[data-kf-viewer]'));

function webglAvailable() {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch (e) {
    return false;
  }
}

const canRender = KF.enable3D !== false && webglAvailable() && !(KF.lowPower && KF.lowPower());

let libsPromise = null;
function loadLibs() {
  libsPromise ||= Promise.all([
    import('three'),
    import('three/addons/loaders/GLTFLoader.js'),
    import('three/addons/loaders/DRACOLoader.js'),
    import('three/addons/controls/OrbitControls.js'),
    import('three/addons/environments/RoomEnvironment.js'),
  ]).then(([THREE, { GLTFLoader }, { DRACOLoader }, { OrbitControls }, { RoomEnvironment }]) => ({
    THREE, GLTFLoader, DRACOLoader, OrbitControls, RoomEnvironment,
  }));
  return libsPromise;
}

/** Soft radial "contact shadow" texture, drawn once. */
function blobTexture(THREE) {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(59,42,28,0.55)');
  grad.addColorStop(0.45, 'rgba(59,42,28,0.25)');
  grad.addColorStop(1, 'rgba(59,42,28,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

class Viewer {
  constructor(el) {
    this.el = el;
    this.mode = el.dataset.kfViewerMode || 'product';
    this.started = false;
    this.visible = false;
    this.active = true; // false while a photo view is shown instead of 3D
    this.anim = null;
    this.idleTimer = 0;
    this.interacted = false;
  }

  modelUrl() {
    const small = window.matchMedia('(max-width: 1023px)').matches;
    return (small && this.el.dataset.modelMobile) || this.el.dataset.model;
  }

  async start() {
    if (this.started) return;
    this.started = true;
    const loading = this.el.querySelector('.kf-viewer__loading');
    if (loading) loading.hidden = false;
    try {
      const L = await loadLibs();
      await this.build(L);
      this.el.classList.add('is-3d', 'is-ready');
      ['.kf-viewer__hint', '.kf-viewer__zoom', '.kf-viewer__tip'].forEach((s) => {
        const n = this.el.querySelector(s);
        if (n) n.hidden = false;
      });
      this.loop();
    } catch (err) {
      console.warn('[kf-3d] falling back to photos:', err);
      this.fail();
    } finally {
      if (loading) loading.hidden = true;
    }
  }

  fail() {
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.domElement.remove();
    }
    this.el.classList.remove('is-3d', 'is-ready');
    this.el.dispatchEvent(new CustomEvent('kf:no3d'));
  }

  async build({ THREE, GLTFLoader, DRACOLoader, OrbitControls, RoomEnvironment }) {
    this.THREE = THREE;
    const el = this.el;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.setAttribute('aria-hidden', 'true');
    this.renderer = renderer;

    const scene = new THREE.Scene();
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = 0.85;
    pmrem.dispose();
    this.scene = scene;

    // Key: large soft warm light front-left (casts the ground shadow); fill: low; rim: very subtle.
    const key = new THREE.DirectionalLight(0xfff1df, 1.6);
    key.position.set(-3, 5, 4);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.radius = 6;
    key.shadow.bias = -0.0004;
    const sc = key.shadow.camera;
    sc.left = -2.5; sc.right = 2.5; sc.top = 2.5; sc.bottom = -2.5; sc.near = 0.5; sc.far = 15;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.35);
    fill.position.set(4, 2, 2);
    scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffe9d2, 0.45);
    rim.position.set(0, 3, -5);
    scene.add(rim);
    scene.add(new THREE.HemisphereLight(0xfff8ef, 0xd9c7b0, 0.25));

    // Load + fit the model: scaled to height, resting on y = 0, centred on x/z.
    const draco = new DRACOLoader().setDecoderPath(KF.dracoPath || 'https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
    const loader = new GLTFLoader().setDRACOLoader(draco);
    const gltf = await loader.loadAsync(this.modelUrl());
    draco.dispose();
    const model = gltf.scene;
    const height = parseFloat(el.dataset.modelHeight || '1');
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const s = height / (size.y || 1);
    model.scale.setScalar(s);
    model.position.set(-center.x * s, -box.min.y * s, -center.z * s);
    model.traverse((o) => {
      if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; }
    });
    const pivot = new THREE.Group();
    pivot.add(model);
    scene.add(pivot);
    this.pivot = pivot;
    const radius = (Math.max(size.x, size.z) * s) / 2;

    // Ground: real shadow from the key light + a soft contact blob.
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(radius * 8, radius * 8), new THREE.ShadowMaterial({ opacity: 0.18 }));
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
    const blob = new THREE.Mesh(
      new THREE.PlaneGeometry(radius * 3.2, radius * 3.2),
      new THREE.MeshBasicMaterial({ map: blobTexture(THREE), transparent: true, depthWrite: false })
    );
    blob.rotation.x = -Math.PI / 2;
    blob.position.y = 0.002;
    scene.add(blob);

    // Materials recoloured by the finish swatches (only these — the model is never swapped).
    this.recolor = { main: [], stitch: [] };
    const upName = el.dataset.upholstery;
    const stName = el.dataset.stitch;
    if (upName) {
      model.traverse((o) => {
        if (!o.isMesh) return;
        (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => {
          if (m.name === upName && !this.recolor.main.includes(m)) this.recolor.main.push(m);
          if (stName && m.name === stName && !this.recolor.stitch.includes(m)) this.recolor.stitch.push(m);
        });
      });
    }
    this.target = { main: new THREE.Color(el.dataset.color || '#a85e32'), stitch: new THREE.Color(el.dataset.stitchColor || el.dataset.color || '#c27e4e') };
    this.recolor.main.forEach((m) => m.color.copy(this.target.main));
    this.recolor.stitch.forEach((m) => m.color.copy(this.target.stitch));

    // Camera + controls
    const camera = new THREE.PerspectiveCamera(30, 1, 0.05, 50);
    this.camera = camera;
    const target = (el.dataset.target || '').split(',').map(Number);
    const camPos = (el.dataset.camera || '').split(',').map(Number);
    const tgt = target.length === 3 && target.every(isFinite) ? new THREE.Vector3(...target) : new THREE.Vector3(0, height * 0.56, 0);
    if (camPos.length === 3 && camPos.every(isFinite)) {
      camera.position.set(...camPos);
    } else {
      const sph = new THREE.Spherical(height * 3.75, 1.2, 0.55);
      camera.position.setFromSpherical(sph).add(tgt);
    }
    const baseDist = camera.position.distanceTo(tgt);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(tgt);
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotate = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    controls.autoRotateSpeed = this.mode === 'tour' ? 0.35 : 0.7;
    controls.minDistance = baseDist * 0.55;
    controls.maxDistance = baseDist * 1.5;
    controls.minPolarAngle = this.mode === 'tour' ? 0.7 : 0.9;
    controls.maxPolarAngle = 1.45;
    controls.zoomSpeed = 0.6;
    controls.rotateSpeed = 0.6;
    controls.update();
    this.controls = controls;

    let startDist = baseDist;
    controls.addEventListener('start', () => {
      this.anim = null;
      controls.autoRotate = false;
      clearTimeout(this.idleTimer);
      startDist = camera.position.distanceTo(controls.target);
    });
    controls.addEventListener('end', () => {
      const zoomed = Math.abs(camera.position.distanceTo(controls.target) - startDist) > 0.05;
      if (KF.track) KF.track.threeD(zoomed ? 'zoom' : 'rotate', el.dataset.label || this.mode);
      this.resumeLater();
    });

    // Plain wheel scrolls the page; Ctrl/⌘ + wheel zooms (pinch on touch still zooms).
    el.addEventListener('wheel', (e) => { if (!e.ctrlKey && !e.metaKey) e.stopPropagation(); }, { capture: true });

    el.querySelectorAll('[data-kf-zoom]').forEach((b) => {
      b.addEventListener('click', () => {
        const r = camera.position.distanceTo(controls.target) * parseFloat(b.dataset.kfZoom);
        this.anim = { radius: THREE.MathUtils.clamp(r, controls.minDistance, controls.maxDistance) };
        this.resumeLater();
        if (KF.track) KF.track.threeD('zoom_button', el.dataset.label || this.mode);
      });
    });

    el.addEventListener('kf:finish', (e) => {
      this.target.main.set(e.detail.color);
      this.target.stitch.set(e.detail.stitch || e.detail.color);
      this.kick();
    });
    el.addEventListener('kf:active', (e) => {
      this.active = e.detail;
      this.kick();
    });

    this.clock = new THREE.Clock();
    this.spherical = new THREE.Spherical();
    this.offset = new THREE.Vector3();
    el.prepend(renderer.domElement);
    this.resize();
    new ResizeObserver(() => this.resize()).observe(el);
  }

  resumeLater() {
    if (!this.controls) return;
    this.controls.autoRotate = false;
    clearTimeout(this.idleTimer);
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.idleTimer = setTimeout(() => { if (this.controls) this.controls.autoRotate = true; }, 3500);
  }

  resize() {
    const w = this.el.clientWidth;
    const h = this.el.clientHeight;
    if (!w || !h || !this.renderer) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.kick();
  }

  shouldRender() {
    return this.visible && this.active && !document.hidden;
  }

  kick() {
    if (this.renderer && this.clock && !this.raf && this.shouldRender()) this.loop();
  }

  loop() {
    this.raf = 0;
    if (!this.renderer || !this.shouldRender()) return;
    this.raf = requestAnimationFrame(() => this.loop());
    const dt = Math.min(this.clock.getDelta(), 0.1);

    const t = 1 - Math.exp(-dt * 6);
    this.recolor.main.forEach((m) => m.color.lerp(this.target.main, t));
    this.recolor.stitch.forEach((m) => m.color.lerp(this.target.stitch, t));

    if (this.anim) {
      this.offset.copy(this.camera.position).sub(this.controls.target);
      this.spherical.setFromVector3(this.offset);
      const k = 1 - Math.exp(-dt * 4.5);
      this.spherical.radius += (this.anim.radius - this.spherical.radius) * k;
      this.offset.setFromSpherical(this.spherical);
      this.camera.position.copy(this.controls.target).add(this.offset);
      if (Math.abs(this.anim.radius - this.spherical.radius) < 0.002) this.anim = null;
    }

    if (this.mode === 'tour' && this.pivot) {
      this.pivot.position.y = Math.sin(this.clock.elapsedTime * 0.8) * 0.008; // subtle idle movement
    }

    this.controls.update(dt);
    this.renderer.render(this.scene, this.camera);
  }
}

if (!canRender) {
  viewers.forEach((el) => el.dispatchEvent(new CustomEvent('kf:no3d')));
} else {
  viewers.forEach((el) => {
    const v = new Viewer(el);
    el._kfViewer = v;

    if (el.dataset.kfViewerLazy === 'manual') {
      // Tour viewer: start when its modal opens, pause when it closes.
      const modal = el.closest('[data-kf-modal]');
      if (modal) {
        modal.addEventListener('kf:open', () => { v.visible = true; v.start(); v.kick(); });
        modal.addEventListener('kf:close', () => { v.visible = false; });
      }
      return;
    }

    // Product viewer: download three.js + model when ~600px away; render only while on screen.
    const near = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { near.disconnect(); v.start(); }
    }, { rootMargin: '600px 0px' });
    near.observe(el);
    new IntersectionObserver((entries) => {
      v.visible = entries[0].isIntersecting;
      v.kick();
    }).observe(el);
  });
  document.addEventListener('visibilitychange', () => viewers.forEach((el) => el._kfViewer && el._kfViewer.kick()));
}
